import { useState, useEffect, useCallback, useRef } from "react";
import {
  auth, onAuthStateChanged,
  signInWithGoogle as fbSignInWithGoogle,
  signOut as fbSignOut,
  fbUserGet, fbUserSet,
  fbSet,
} from "./firebase";

// ─── KEYS that form the full game state snapshot ───────────────
const GAME_KEYS = [
  "inventory", "coins", "totalCoins", "rollCount",
  "pity", "boosts", "prestige", "achievements",
  "favourites", "pullCounts", "pullStats", "rollHistory",
  "streak", "lastClaim",
];

// ─── Read all game state from localStorage ────────────────────
function readLocalState() {
  const state = {};
  GAME_KEYS.forEach(key => {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) state[key] = JSON.parse(v);
    } catch {}
  });
  return state;
}

// ─── Write a cloud snapshot back into localStorage ────────────
function hydrateLocalState(cloudState) {
  GAME_KEYS.forEach(key => {
    if (cloudState[key] !== undefined) {
      try {
        localStorage.setItem(key, JSON.stringify(cloudState[key]));
      } catch {}
    }
  });
}

// ─── Wipe game state from localStorage (on sign-out) ─────────
function clearLocalGameState() {
  GAME_KEYS.forEach(key => {
    try { localStorage.removeItem(key); } catch {}
  });
}

// ─── HOOK ─────────────────────────────────────────────────────
export default function useAuth() {
  const [user,         setUser]         = useState(null);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [saveStatus,   setSaveStatus]   = useState("idle");
  const [lastSaved,    setLastSaved]    = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const debounceRef                     = useRef(null);
  const isMigratedRef                   = useRef(false);

  // ── Save current localStorage state to Firebase ─────────────
  const saveToCloud = useCallback(async (uid, force = false) => {
    if (!uid) return;

    const doSave = async () => {
      setSaveStatus("saving");
      try {
        const state = { ...readLocalState(), savedAt: Date.now() };
        await fbUserSet(uid, "gameState", state);

        const playerName = localStorage.getItem("playerName");
        if (playerName) {
          const inv = state.inventory || [];
          const RARITY_ORDER = ["Common","Uncommon","Rare","Epic","Legendary","Mythic","Ultra"];
          const rarest = inv.length
            ? inv.reduce((b, i) =>
                RARITY_ORDER.indexOf(i.rarity) > RARITY_ORDER.indexOf(b) ? i.rarity : b,
                inv[0].rarity)
            : "—";
          await fbSet(`lb/${uid}`, {
            name:           playerName,
            rolls:          state.rollCount   || 0,
            collected:      inv.length,
            totalCoins:     state.totalCoins  || 0,
            prestige:       state.prestige    || 0,
            battleTrophies: parseInt(localStorage.getItem("battleTrophies") || "0", 10),
            rarest,
            updatedAt:      Date.now(),
          });
        }

        setSaveStatus("saved");
        setLastSaved(Date.now());
      } catch {
        setSaveStatus("error");
      }
    };

    if (force) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      await doSave();
    } else {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(doSave, 60000);
    }
  }, []);

  // ── Load cloud state → localStorage → reload ────────────────
  // Always trust the cloud when signing in. Local state was cleared
  // on sign-out so there's nothing to conflict with.
  const loadFromCloud = useCallback(async (uid) => {
    const cloudState = await fbUserGet(uid, "gameState");

    if (!cloudState) {
      // First ever sign-in — push current local state up to cloud
      const localState = readLocalState();
      await fbUserSet(uid, "gameState", { ...localState, savedAt: Date.now() });
      isMigratedRef.current = true;
      window.location.reload();
      return;
    }

    // Hydrate localStorage with cloud data, then reload so
    // useGameState re-initialises from the correct values
    hydrateLocalState(cloudState);
    isMigratedRef.current = true;
    window.location.reload();
  }, []);

  // ── Resolve conflict manually (player chose) ─────────────────
  const resolveConflictChoice = useCallback(async (choice, uid) => {
    if (!conflictData || !uid) return;
    if (choice === "cloud") {
      hydrateLocalState(conflictData.cloud);
    } else {
      await fbUserSet(uid, "gameState", { ...conflictData.local, savedAt: Date.now() });
    }
    setConflictData(null);
    isMigratedRef.current = true;
    window.location.reload();
  }, [conflictData]);

  // ── Auth state listener ──────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        localStorage.setItem("playerId", firebaseUser.uid);

        if (!isMigratedRef.current) {
          // Check if this is a fresh sign-in (not a page reload while
          // already signed in). We use sessionStorage as a flag —
          // it survives JS reloads within the same tab session only
          // when we explicitly set it, not across fresh tab opens.
          const justHydrated = sessionStorage.getItem("cloudHydrated");

          if (!justHydrated) {
            // Fresh sign-in — load cloud data, set flag, reload
            sessionStorage.setItem("cloudHydrated", "1");
            await loadFromCloud(firebaseUser.uid);
            // loadFromCloud calls window.location.reload() after hydrating.
            // The flag persists through the reload so we don't loop.
          } else {
            // Already hydrated this session — just mark migrated and continue
            isMigratedRef.current = true;
          }
        }
      } else {
        setUser(null);
        isMigratedRef.current = false;
        // Clear the hydration flag so the next sign-in loads fresh from cloud
        sessionStorage.removeItem("cloudHydrated");
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, [loadFromCloud]);

  // ── Periodic autosave when logged in ─────────────────────────
  useEffect(() => {
    if (!user) return;
    // Save on tab blur/hide
    const handleBlur = () => saveToCloud(user.uid, true);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) saveToCloud(user.uid, true);
    });
    // Kick off the first debounced save
    saveToCloud(user.uid);
    return () => {
      window.removeEventListener("blur", handleBlur);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [user, saveToCloud]);

  // ── Public API ───────────────────────────────────────────────
  const signInWithGoogle = useCallback(async () => {
    const u = await fbSignInWithGoogle();
    return u;
  }, []);

  const signOut = useCallback(async () => {
    if (user) await saveToCloud(user.uid, true);
    clearLocalGameState();
    sessionStorage.removeItem("cloudHydrated");
    await fbSignOut();
    setUser(null);
    setSaveStatus("idle");
    setLastSaved(null);
    isMigratedRef.current = false;
    window.location.reload();
  }, [user, saveToCloud]);

  return {
    user,
    authLoading,
    saveStatus,
    lastSaved,
    conflictData,
    signInWithGoogle,
    signOut,
    saveToCloud,
    resolveConflictChoice,
  };
}